// React Query
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

// Utils
import {
  deleteRequest as del,
  postRequest as post,
} from '../utils/HttpMethods';
import * as Sentry from '@sentry/react';
import type { ResubmitClaimTariffResponse } from '@billing/validation/action';
import type { Invoice, InvoiceType } from '@billing/validation/invoice';
import { Claim } from 'interfaces';
import { useCurrentSiteStore } from 'store';

interface ResubmitClaimTariffParams {
  claimID: string;
  tariffID: string;
  createdBy: string;
}

export interface UploadClaimInvoiceParams {
  claimID: string;
  siteID: string;
  uploadedFileKey: string;
  amount: number;
  invoiceType: InvoiceType;
  selectedTariffIDs?: string[];
  internalComment?: string;
  replaceInvoiceID?: string;
}

interface DeleteClaimInvoiceParams {
  invoiceID: string;
}

interface DeleteClaimInvoiceResponse {
  deletedInvoiceID: string;
  claim: Claim | null;
}

const resubmitClaimTariff = async ({
  claimID,
  tariffID,
  createdBy,
}: ResubmitClaimTariffParams): Promise<ResubmitClaimTariffResponse> => {
  try {
    const response: ResubmitClaimTariffResponse = await post(
      'QBilling',
      `/action/claim/tariff/resubmit`,
      {
        claimID,
        tariffID,
        createdBy,
      }
    );

    return response;
  } catch (error) {
    Sentry.setContext('payload', { claimID, tariffID, createdBy });
    Sentry.captureException('Failed to resubmit claim tariff', error);
  }
};

export const useResubmitClaimTariff =
  (): UseMutationResult<ResubmitClaimTariffResponse> => {
    const queryClient = useQueryClient();
    const { id: siteID } = useCurrentSiteStore();
    return useMutation({
      mutationKey: ['resubmitClaimTariff'],
      mutationFn: resubmitClaimTariff,
      onError: (error) => {
        Sentry.captureException(error);
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ['claim', siteID],
        });
        return data;
      },
      retry: 0,
    });
  };

const uploadClaimInvoice = async ({
  claimID,
  siteID,
  uploadedFileKey,
  amount,
  invoiceType,
  selectedTariffIDs,
  internalComment,
  replaceInvoiceID,
}: UploadClaimInvoiceParams): Promise<Invoice | null> => {
  try {
    const response = await post<Invoice>(
      'QBilling',
      `/action/claim/invoice/upload`,
      {
        claimID,
        siteID,
        uploadedFileKey,
        amount,
        invoiceType,
        selectedTariffIDs,
        internalComment,
        replaceInvoiceID,
      }
    );
    return response;
  } catch (error) {
    Sentry.setContext('payload', {
      claimID,
      siteID,
      uploadedFileKey,
      amount,
      invoiceType,
      selectedTariffIDs,
      internalComment,
      replaceInvoiceID,
    });
    Sentry.captureException(error);
    throw error;
  }
};

export const useUploadClaimInvoice = (): UseMutationResult<
  Invoice | null,
  Error,
  UploadClaimInvoiceParams
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['uploadClaimInvoice'],
    mutationFn: uploadClaimInvoice,
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['claim'],
      });
    },
    retry: 0,
  });
};

const deleteClaimInvoice = async ({
  invoiceID,
}: DeleteClaimInvoiceParams): Promise<DeleteClaimInvoiceResponse | null> => {
  try {
    const response = await del<DeleteClaimInvoiceResponse>(
      'QBilling',
      `/resource/claim/invoice`,
      {
        invoiceID,
      }
    );
    return response;
  } catch (error) {
    Sentry.setContext('payload', { invoiceID });
    Sentry.captureException(error);
    throw error;
  }
};

export const useDeleteClaimInvoice = (): UseMutationResult<
  DeleteClaimInvoiceResponse | null,
  Error,
  DeleteClaimInvoiceParams
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['deleteClaimInvoice'],
    mutationFn: deleteClaimInvoice,
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['claim'],
      });
    },
    retry: 0,
  });
};
